import { useEffect, useRef } from "react";
import { Button } from "../ui/Button";

export type ExitConfirmDialogProps = {
  open: boolean;
  completedCount: number;
  totalCount: number;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Uses the native HTMLDialogElement so the browser handles backdrop,
 * focus trap, and ESC-to-close.
 */
export function ExitConfirmDialog({
  open,
  completedCount,
  totalCount,
  onCancel,
  onConfirm,
}: ExitConfirmDialogProps) {
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  function handleBackdropClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (e.target === ref.current) onCancel();
  }

  function handleCancel(e: React.SyntheticEvent<HTMLDialogElement>) {
    e.preventDefault();
    onCancel();
  }

  const allDone = completedCount >= totalCount;
  const hasProgress = completedCount > 0;

  const eyebrow = "Examen sluiten?";
  const body = !hasProgress
    ? "U hebt nog niets opgenomen. Wilt u de oefening stoppen?"
    : allDone
      ? "U hebt alle beurten gedaan. Klaar om naar het rapport te gaan?"
      : `U hebt ${completedCount} van ${totalCount} beurten gedaan. U kunt nu naar het rapport, of teruggaan om verder te oefenen.`;

  const confirmLabel = hasProgress ? "Naar het rapport" : "Sluiten";

  return (
    <dialog
      ref={ref}
      onClick={handleBackdropClick}
      onCancel={handleCancel}
      aria-labelledby="exit-confirm-title"
      aria-describedby="exit-confirm-body"
      className="
        m-auto rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)]
        text-[var(--ink)] shadow-[var(--shadow)] p-0 max-w-md w-[calc(100%-2rem)]
        backdrop:bg-[var(--ink)]/40 backdrop:backdrop-blur-sm
      "
    >
      <div className="p-6 space-y-4">
        <p
          className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]"
          aria-hidden
        >
          Bevestiging
        </p>
        <h2
          id="exit-confirm-title"
          className="font-serif-italic text-2xl text-[var(--ink)]"
        >
          {eyebrow}
        </h2>
        <p id="exit-confirm-body" className="text-sm leading-relaxed text-[var(--ink-muted)]">
          {body}
        </p>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" size="md" onClick={onCancel} autoFocus>
            Verder oefenen
          </Button>
          <Button variant="primary" size="md" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
}
